import React, { useState, useEffect } from 'react';
import { useAuth } from '@/components/Auth/AuthContext';
import PageHeader from '@/components/Layout/PageHeader';
import { useIsNarrowScreen } from '@/hooks/useMediaQuery';
import { getPrimaryRoleLabel } from '@/utils/swaRole';
import { formatPhoneForDisplay } from '@/utils/phone';
import { LIST_SEARCH_DEBOUNCE_MS } from '@/constants/search';
import { confirmDeletionByTyping } from '@/utils/deleteConfirmation';
import { Button, Card, DataRow, EmptyState, Input, Modal, Textarea } from '@/ui/components';
import '@/pages/dataGridFocus.css';

type ClientSortBy = 'id' | 'name' | 'created';
type SortDirection = 'asc' | 'desc';
const LIST_PAGE_SIZE = 500;

type ClientEditorForm = {
  clientId: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  address: string;
  abn: string;
};

function getDefaultSortDirection(sortBy: ClientSortBy): SortDirection {
  return 'desc';
}

function emptyClientEditorForm(): ClientEditorForm {
  return {
    clientId: '',
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    abn: '',
  };
}

function mapClientToEditorForm(client?: any): ClientEditorForm {
  if (!client) return emptyClientEditorForm();
  return {
    clientId: client.ClientId != null ? String(client.ClientId) : '',
    name: String(client.Name || '').trim(),
    company: String(client.Company || '').trim(),
    email: String(client.Email || '').trim(),
    phone: formatPhoneForDisplay(client.Phone) || String(client.Phone || '').trim(),
    address: String(client.Address || '').trim(),
    abn: String(client.Abn || '').trim(),
  };
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const ClientList: React.FC = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<ClientSortBy>('created');
  const [sortDirection, setSortDirection] = useState<SortDirection>(getDefaultSortDirection('created'));
  const playSuccess = () => {};
  const playError = () => {};
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorBusy, setEditorBusy] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [editorForm, setEditorForm] = useState<ClientEditorForm>(emptyClientEditorForm());
  const [deleteBusyClientId, setDeleteBusyClientId] = useState<string | null>(null);
  const { user, isAuthenticated } = useAuth();
  const roleLabel = getPrimaryRoleLabel(user?.userRoles, user?.userDetails);
  const isNarrowScreen = useIsNarrowScreen(640);

  useEffect(() => {
    // Wait for auth to be ready
    if (!isAuthenticated || !user) return;
    const timer = window.setTimeout(() => {
      void loadClients(searchQuery.trim());
    }, searchQuery.trim() ? LIST_SEARCH_DEBOUNCE_MS : 0);

    return () => window.clearTimeout(timer);
  }, [isAuthenticated, user, searchQuery]);

  const loadClients = async (query: string = searchQuery.trim()) => {
    setLoading(true);
    setError(null);

    try {
      // Dynamic import to ensure AuthContext is initialized first
      const { azureSqlService } = await import('@/services/azureSqlService.js');

      // Get clients from Azure SQL Database (Clients table)
      const clientData = await azureSqlService.listClients({
        page: 1,
        pageSize: LIST_PAGE_SIZE,
        query: query ? { q: query } : null,
        useCache: !query,
      });
      setClients(clientData);
      playSuccess(); // 🔊 Success notification
      console.log('Clients loaded from Azure SQL:', clientData.length);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      playError(); // 🔊 Error notification
      console.error('Error loading clients:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    void loadClients();
  };

  const closeEditor = () => {
    if (editorBusy) return;
    setEditorOpen(false);
    setEditorError(null);
    setEditorForm(emptyClientEditorForm());
  };

  const openCreateClient = () => {
    setNotice(null);
    setError(null);
    setEditorError(null);
    setEditorForm(emptyClientEditorForm());
    setEditorOpen(true);
  };

  const openEditClient = (client: any) => {
    setNotice(null);
    setError(null);
    setEditorError(null);
    setEditorForm(mapClientToEditorForm(client));
    setEditorOpen(true);
  };

  const updateEditorField = (field: keyof ClientEditorForm, value: string) => {
    setEditorForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveClient = async () => {
    const trimmedName = editorForm.name.trim();
    if (!trimmedName) {
      setEditorError('Client name is required.');
      return;
    }

    setEditorBusy(true);
    setEditorError(null);
    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.saveClient({
        clientId: editorForm.clientId || undefined,
        name: trimmedName,
        company: editorForm.company.trim() || undefined,
        email: editorForm.email.trim() || undefined,
        phone: editorForm.phone.trim() || undefined,
        address: editorForm.address.trim() || undefined,
        abn: editorForm.abn.trim() || undefined,
      });
      setNotice(editorForm.clientId ? `Updated client ${trimmedName}.` : `Created client ${trimmedName}.`);
      setEditorOpen(false);
      setEditorForm(emptyClientEditorForm());
      await loadClients(searchQuery.trim());
      playSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save client.';
      setEditorError(message);
      playError();
    } finally {
      setEditorBusy(false);
    }
  };

  const handleDeleteClient = async (client: any) => {
    const clientId = String(client?.ClientId ?? client?.Id ?? '').trim();
    const clientName = String(client?.Name || '').trim();
    if (!clientId) {
      setNotice(null);
      setError('Client ID is required before deleting this client.');
      playError();
      return;
    }

    if (!confirmDeletionByTyping({
      entityLabel: `client ${clientName || clientId}`,
      confirmationLabel: 'client ID',
      identifiers: [clientId],
      softDelete: true,
      extraMessage: 'This only marks the client row as deleted or inactive in the database. Linked historical records are not removed.',
    })) {
      return;
    }

    setDeleteBusyClientId(clientId);
    setNotice(null);
    setError(null);

    try {
      const { azureSqlService } = await import('@/services/azureSqlService.js');
      await azureSqlService.deleteClient(clientId);
      setClients((current) => current.filter((item: any) => String(item?.ClientId ?? item?.Id ?? '').trim() !== clientId));
      setNotice(`Deleted client ${clientName || clientId}.`);
      await loadClients(searchQuery.trim());
      playSuccess();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete client.';
      setError(`Failed to delete client: ${message}`);
      playError();
    } finally {
      setDeleteBusyClientId(null);
    }
  };

  const filteredClients = clients.filter((client: any) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    const hay = [
      client?.Name,
      client?.Email,
      client?.Phone,
      client?.Address,
      client?.ClientId != null ? String(client.ClientId) : null,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  });

  const sortedClients = [...filteredClients].sort((a: any, b: any) => {
    let comparison = 0;
    if (sortBy === 'created') {
      const da = new Date(a?.CreatedDate || 0).getTime();
      const db = new Date(b?.CreatedDate || 0).getTime();
      comparison = da - db;
    } else if (sortBy === 'id') {
      const idA = Number(a?.ClientId ?? a?.Id ?? 0);
      const idB = Number(b?.ClientId ?? b?.Id ?? 0);
      comparison = idA - idB;
    } else {
      comparison = String(a?.Name || '').localeCompare(String(b?.Name || ''));
    }
    return sortDirection === 'desc' ? comparison * -1 : comparison;
  });
  const visibleClients = sortedClients;

  const handleSortChange = (value: string) => {
    const nextSortBy = value as ClientSortBy;
    setSortBy(nextSortBy);
    setSortDirection(getDefaultSortDirection(nextSortBy));
  };

  const toggleSortDirection = () => {
    setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
  };

  return (
    <div style={{ backgroundColor: 'var(--color-background)', minHeight: '100vh' }}>
      <PageHeader
        title="Clients"
        subtitle="Browse and search contact records across the client list."
        itemCount={visibleClients.length}
        gradientColor="linear-gradient(135deg, #0f8a5f 0%, #0b6b49 100%)"
        icon="👥"
        userLabel={user?.userDetails}
        roleLabel={roleLabel}
        density="compact"
        searchEnabled={true}
        searchQuery={searchQuery}
        searchPlaceholder="Search clients (name, email, phone, address, ID)"
        onSearchChange={setSearchQuery}
        sortEnabled={true}
        sortBy={sortBy}
        sortDirection={sortDirection}
        sortOptions={[
          { value: 'id', label: 'ID' },
          { value: 'created', label: 'Created' },
          { value: 'name', label: 'Name' },
        ]}
        onSortChange={handleSortChange}
        onSortDirectionToggle={toggleSortDirection}
        actions={[
          {
            label: loading ? 'Loading...' : 'Refresh',
            onClick: handleRefresh,
            variant: 'secondary',
            disabled: loading,
          },
          {
            label: 'Create client',
            onClick: openCreateClient,
            variant: 'primary',
          },
        ]}
        controlBarDensity="compact"
      />

      <div style={{ padding: isNarrowScreen ? '16px 12px 96px' : '20px 24px 96px', maxWidth: '1720px', margin: '0 auto' }}>

        {/* Error Message */}
        {error && (
          <Card padding="md">
            <div style={{ color: 'var(--color-danger)' }}>
              <strong>Error:</strong> {error}
            </div>
          </Card>
        )}

        {notice && (
          <Card padding="md">
            <div style={{ color: 'var(--color-success)' }}>
              <strong>Saved:</strong> {notice}
            </div>
          </Card>
        )}

        {/* Loading State */}
        {loading && (
          <Card padding="lg">
            <div style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
              Loading clients...
            </div>
          </Card>
        )}

        {!loading && visibleClients.length > 0 && (
          <div className="client-mobile-list">
            {visibleClients.map((client: any) => (
              <DataRow
                key={client.ClientId}
                density="compact"
                className="client-data-row"
                title={(
                  <div className="client-data-row__title-line">
                    <span className="client-data-row__name">{client.Name || '-'}</span>
                    <span className="client-data-row__id">ID {client.ClientId}</span>
                  </div>
                )}
                subtitle={client.Email || '-'}
                description={(
                  <span className="client-data-row__compact-meta">
                    {[
                      `Address: ${client.Address || '-'}`,
                    ].join('   ')}
                  </span>
                )}
                amount={formatDate(client.CreatedDate)}
                meta={formatPhoneForDisplay(client.Phone) || '-'}
                status={(
                  <div className="client-data-row__status-line">
                    <Button
                      size="sm"
                      variant="secondary"
                      className="client-data-row__action"
                      onClick={() => openEditClient(client)}
                      disabled={Boolean(deleteBusyClientId)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      className="client-data-row__action"
                      onClick={() => { void handleDeleteClient(client); }}
                      disabled={Boolean(deleteBusyClientId)}
                    >
                      Delete
                    </Button>
                  </div>
                )}
              />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && visibleClients.length === 0 && !error && (
          <Card padding="none">
            <EmptyState
              icon={<span style={{ fontSize: '48px' }}>📋</span>}
              title="No clients found"
              description="Get started by creating your first client"
              action={<Button onClick={openCreateClient}>Create client</Button>}
            />
          </Card>
        )}
      </div>

      <Modal
        open={editorOpen}
        onClose={closeEditor}
        title={editorForm.clientId ? `Edit client · ${editorForm.name || editorForm.clientId}` : 'Create client'}
        size="lg"
        footer={(
          <>
            <Button variant="secondary" onClick={closeEditor} disabled={editorBusy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => { void handleSaveClient(); }} disabled={editorBusy}>
              {editorBusy ? 'Saving...' : (editorForm.clientId ? 'Save changes' : 'Create client')}
            </Button>
          </>
        )}
      >
        <div style={{ display: 'grid', gap: 16 }}>
          {editorError ? (
            <div className="dgf-notice dgf-notice--danger">{editorError}</div>
          ) : null}

          <div style={{ display: 'grid', gridTemplateColumns: isNarrowScreen ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
            <Input
              label="Name"
              value={editorForm.name}
              onChange={(event) => updateEditorField('name', event.target.value)}
              fullWidth
              required
              disabled={editorBusy}
            />
            <Input
              label="Company"
              value={editorForm.company}
              onChange={(event) => updateEditorField('company', event.target.value)}
              fullWidth
              disabled={editorBusy}
            />
            <Input
              label="Email"
              type="email"
              value={editorForm.email}
              onChange={(event) => updateEditorField('email', event.target.value)}
              fullWidth
              disabled={editorBusy}
            />
            <Input
              label="Phone"
              value={editorForm.phone}
              onChange={(event) => updateEditorField('phone', event.target.value)}
              fullWidth
              disabled={editorBusy}
            />
            <Input
              label="ABN"
              value={editorForm.abn}
              onChange={(event) => updateEditorField('abn', event.target.value)}
              fullWidth
              disabled={editorBusy}
            />
          </div>

          <Textarea
            label="Address"
            value={editorForm.address}
            onChange={(event) => updateEditorField('address', event.target.value)}
            rows={4}
            fullWidth
            disabled={editorBusy}
          />
        </div>
      </Modal>
    </div>
  );
};

export default ClientList;
